import { Button, Col, InputNumber, Modal, Space, Spin, Typography } from 'antd';
import React, { FC, useCallback, useState } from 'react';
import {
  UserOutlined,
  TeamOutlined,
  ExclamationCircleOutlined,
  AlignLeftOutlined,
  EditOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { useConnection } from '../../../connection/Application';
import Form from '../../../form/Form';
import ReactHtmlParser from '../../../utils/SafeHtmlParser';
const { Title, Text } = Typography;
const { confirm } = Modal;

export type Props = {
  setLoading?: (x: boolean) => void;
  type: 'user' | 'supervisor';
  value?: { value?: number; comment?: string };
  onSubmit?: () => void;
  goalId: number;
  goal: string;
  mode: 'edit' | 'view';
  disableSet: boolean;
  objectiveType?: string;
};

const MarksPopOver: FC<Props> = (props) => {
  const [value, setValue] = useState<number>(props?.value?.value ?? 100);
  const [comment, setComment] = useState<string>(props?.value?.comment ?? '');
  const [visible, setVisible] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [evaluationModelOpen, setEvaluationModelOpen] = useState<boolean>(false);
  const connection = useConnection();
  const [form] = Form.useForm();

  // useEffect(() => {
  //   if (props.value) {
  //     setValue(props.value.value ?? 100);
  //     setComment(props.value.comment ?? undefined);
  //   }
  // }, [props.value]);

  const showSubmitModal = useCallback(
    (l: string) => {
      setVisible(true);
      props?.setLoading?.(true);
      confirm({
        title: 'Submitting your marks',
        icon: <ExclamationCircleOutlined />,
        content: props.type === 'user' ? `If you submit you won't be able to change` : '',
        zIndex: 2020,
        okText: 'Yes,Submit!',
        centered: true,
        okType: 'primary',
        cancelText: 'Cancel',
        onCancel() {
          console.log('Cancel');
          setVisible(false);
        },
        onOk() {
          setLoading(true);
          connection
            .patch(`goals/${props.objectiveType}/goal-mark/${props.goalId}`, {
              userMark: props.type === 'user' ? value : 0,
              supervisorMark: props.type === 'supervisor' ? value : 0,
              userComment: props.type === 'user' ? l : '',
              supervisorComment: props.type === 'supervisor' ? l : '',
            })
            .then(() => {
              props?.onSubmit?.();
              setEvaluationModelOpen(false);
              setLoading(false);
            })
            .catch((e1) => {
              setVisible(false);
              setLoading(false);
              console.error(e1);
              props?.setLoading?.(false);
            });
        },
      });
    },
    [connection, props, value],
  );

  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
        }}>
        <Typography style={{ cursor: 'pointer' }}>
          {props.value?.value !== undefined ? (
            <div style={{ display: 'flex' }}>
              {`${props.value?.value} %`}
              {/* eslint-disable-next-line */}
              <div onClick={() => setEvaluationModelOpen(true)} style={{ paddingLeft: '10px' }}>
                {props.mode === 'edit' ? <EditOutlined /> : <EyeOutlined />}
              </div>
            </div>
          ) : (
            <Button
              onClick={() => setEvaluationModelOpen(true)}
              disabled={visible || props.disableSet}
              type={'primary'}
              size={'small'}>
              Set
            </Button>
          )}
        </Typography>
      </div>
      <Modal
        open={evaluationModelOpen}
        title={`${props.type === 'user' ? 'Self' : 'Supervisor'} Evaluation`}
        onCancel={() => setEvaluationModelOpen(false)}
        footer={
          props.mode === 'edit'
            ? [
                <Button
                  key="cancel"
                  type="default"
                  disabled={loading}
                  onClick={() => setEvaluationModelOpen(false)}>
                  Cancel
                </Button>,
                <Button key="submit" type="primary" loading={loading} onClick={() => form.submit()}>
                  Submit
                </Button>,
              ]
            : null
        }
        cancelButtonProps={{ disabled: loading }}
        destroyOnClose={true}>
        <Spin spinning={loading}>
          <Title level={4}>Objective: {props.goal}</Title>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <Space size={40} style={{ display: 'flex', alignItems: 'baseline' }}>
              <Space style={{ marginBottom: 20 }}>
                {props.type === 'user' ? (
                  <>
                    <UserOutlined style={{ color: 'rgba(0, 0, 0, 0.45)' }} />
                    <Text type="secondary">Self Evaluation</Text>
                  </>
                ) : (
                  <>
                    <TeamOutlined style={{ color: 'rgba(0, 0, 0, 0.45)' }} />
                    <Text type="secondary">Supervisor Evaluation</Text>
                  </>
                )}
              </Space>
              {props.mode === 'edit' ? (
                <>
                  <InputNumber
                    min={0}
                    max={100}
                    value={value}
                    style={{ width: props.type === 'user' ? 140 : 97 }}
                    formatter={(value) => `${value} %`}
                    onChange={(e) => setValue(e ?? 0)}
                  />
                </>
              ) : (
                <Typography>{`${value} %`}</Typography>
              )}
            </Space>
            {props.mode === 'edit' && (
              <Form
                initialValues={{ comment }}
                layout={'vertical'}
                form={form}
                onFinish={(l) => {
                  setComment(l.comment);
                  showSubmitModal(l.comment);
                }}>
                <Form.Items
                  schema={[
                    {
                      id: 'comment',
                      name: 'Add Comment',
                      type: 'richText',
                      props: {
                        autosize: { minRows: 8, maxRows: 12 },
                        maxLength: 10000,
                      },
                      options: {
                        rules: [
                          // { required: true, whitespace: true, message: 'Comment is required' },
                          { max: 10000, message: 'Only 10000 characters are allowed.' },
                        ],
                      },
                    },
                  ]}
                />
              </Form>
            )}
            {props.mode === 'view' && (
              <Col>
                <Space>
                  <AlignLeftOutlined />
                  <Typography>Comment</Typography>
                </Space>

                <div style={{ border: '1px solid rgba(56,54,48,.16)', padding: 5, minHeight: 30 }}>
                  {ReactHtmlParser(comment ?? '')}
                </div>
              </Col>
            )}
          </div>
        </Spin>
      </Modal>
      {/*<Popover*/}
      {/*  onVisibleChange={(v) => {*/}
      {/*    if (v) {*/}
      {/*      // props.onSubmit?.();*/}
      {/*    }*/}
      {/*  }}*/}
      {/*  zIndex={2000}*/}
      {/*  style={{ position: 'fixed', paddingRight: '10px' }}*/}
      {/*  destroyTooltipOnHide*/}
      {/*  content={content}*/}
      {/*  trigger="click">*/}
      {/*</Popover>*/}
    </>
  );
};

export default MarksPopOver;
